import React, { useEffect, useMemo, useState } from 'react';
import { Badge, DataTable, EmptyValue, RowActions, IdLink, type DataTableColumn } from '@/components/ui';
import { ContractArea } from '../../../services/areasService';
import { useClientSort } from '../../../utils/useClientSort';
import ServerPagination from '../../../components/ServerPagination';
import { AreaParentLink, ServiceAreaParentLink } from './AreaRelationLink';

interface ContractAreasTableProps {
  contractAreas: ContractArea[];
  onEdit: (contractArea: ContractArea) => void;
  onDelete: (id: number) => void;
  canEdit: boolean;
  canDelete: boolean;
  /** Open Foreman Areas filtered to this parent (W-M22). */
  onShowForemanArea?: (foremanAreaName: string) => void;
  /** Open Service Areas filtered to this parent (W-M22). */
  onShowServiceArea?: (serviceAreaName: string) => void;
  emptyMessage?: string;
}

const SORT_VALUES: Record<string, (ca: ContractArea) => string | number | null | undefined> = {
  name: (ca) => ca.name,
  code: (ca) => ca.code,
  assets: (ca) => ca.assetsCount ?? ca.assets?.length ?? 0,
};

const ContractAreasTable: React.FC<ContractAreasTableProps> = ({
  contractAreas,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  onShowForemanArea,
  onShowServiceArea,
  emptyMessage = 'No contract areas found.',
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { sorted, sortBy, sortDir, onSort } = useClientSort(contractAreas, SORT_VALUES);
  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    onSort(key, dir);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [contractAreas]);

  const total = contractAreas.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  if (contractAreas.length === 0) {
    return (
      <div className="tw-text-center tw-py-4">
        <p className="tw-text-content-muted">{emptyMessage}</p>
      </div>
    );
  }

  const contractAreaColumns: DataTableColumn<ContractArea>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      mobile: 'title',
      // V3-025 / ACT-01: the name is the link that opens the record (when the user can edit it).
      cell: (ca) => (canEdit ? <IdLink onClick={() => onEdit(ca)}>{ca.name}</IdLink> : <strong>{ca.name}</strong>),
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      mobileLabel: 'Foreman Area',
      cell: (ca) =>
        ca.foremanAreas && ca.foremanAreas.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">
            {ca.foremanAreas.map((fa) => (
              <AreaParentLink
                key={fa.id}
                name={fa.name}
                onOpen={() => onShowForemanArea?.(fa.name)}
                title={`Show ${fa.name} on the Foreman Areas tab`}
              />
            ))}
          </div>
        ) : (
          <span className="tw-text-content-muted">No foreman areas</span>
        ),
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      mobileLabel: 'Service Area',
      cell: (ca) =>
        ca.foremanAreas && ca.foremanAreas.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {Array.from(
              new Map(
                ca.foremanAreas
                  .filter((fa) => fa.serviceArea?.name)
                  .map((fa) => [fa.serviceArea!.name, fa.serviceArea] as const),
              ).values(),
            ).map((sa) => (
              <ServiceAreaParentLink key={sa.name} serviceArea={sa} onOpen={(name) => onShowServiceArea?.(name)} />
            ))}
          </div>
        ) : (
          <span className="tw-text-content-muted">No service areas</span>
        ),
    },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      mobileLabel: 'Code',
      cell: (ca) =>
        // V2-032: Code is plain text, not a pill (STAT-02).
        ca.code ? ca.code : <EmptyValue />,
    },
    {
      key: 'assets',
      header: 'Assets',
      sortable: true,
      mobileLabel: 'Assets',
      cell: (ca) => <Badge bg="warning">{ca.assetsCount ?? ca.assets?.length ?? 0} assets</Badge>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      hidden: !(canEdit || canDelete),
      cell: (ca) => {
        const assetCount = ca.assetsCount ?? ca.assets?.length ?? 0;
        return (
          <RowActions
            actions={[
              {
                kind: 'edit',
                label: 'Edit Contract Area',
                onClick: () => onEdit(ca),
                hidden: !canEdit,
              },
              {
                kind: 'delete',
                // The disabled reason rides in the label — a kebab item has no hover target.
                label: assetCount > 0 ? 'Delete Contract Area – has assets' : 'Delete Contract Area',
                onClick: () => onDelete(ca.id),
                disabled: assetCount > 0,
                hidden: !canDelete,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <DataTable
        columns={contractAreaColumns}
        data={pageSlice}
        rowKey={(ca) => ca.id}
        striped
        bordered
        hover
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <ServerPagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={total}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        className="tw-mt-3"
      />
    </div>
  );
};

export default ContractAreasTable;
